import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  deleteGatewayZone,
  getGatewayZoneAssets,
  getZoneAckInfoByGateway,
  getZonesByGateway,
  getZoneUnAckInfoByGateway,
  saveToGatewayZone,
} from "src/core/api/services/command-module/zone.service";
import ModalComponent from "src/core/components/modal/modal.component";
import NewTableComponent from "src/core/components/table/new-table.component";
import SearchBarComponent from "src/modules/admin/components/search-bar/search-bar.component";
import { safeAtob } from "src/shared/helpers/enode-decode-string";
import { displayToast } from "src/shared/helpers/toaster.helper";
import type { Zone, ZoneAsset } from "../components/types";
import { ZoneCardList } from "./components/zone-card-list.component";
import {
  useZoneConfigurationAck,
  ZoneConfigurationAckProvider,
} from "./components/zone-configuration-ack-provider.component";
import { ZoneFormModal } from "./components/zone-form-modal.component";
import { ZoneGeneralConfigurationModal } from "./components/zone-general-configuration-modal.component";
import "./zones.scss";

const PAGE_SIZE = 10;
const ZONE_ASSETS_PAGE_SIZE = 10;

const ZonesContent = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const assetCode = id ? safeAtob(id) : null;

  const assetDetails = useSelector((state: any) => state.asset.assetDetails);
  const {
    states: zoneConfigurationStates,
    start: startZoneConfiguration,
    fail: failZoneConfiguration,
  } = useZoneConfigurationAck();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [configuringZone, setConfiguringZone] = useState<Zone | null>(null);
  /* Temporarily disabled with zone-list selection/send actions.
  const [selectedZoneIds, setSelectedZoneIds] = useState<number[]>([]);
  */
  const [initialAssignedAssets, setInitialAssignedAssets] = useState<ZoneAsset[] | undefined>();
  const [gatewayCode, setGatewayCode] = useState<string>("");
  const [pageNumber, setPageNumber] = useState(1);
  const [zoneSearchText, setZoneSearchText] = useState("");
  const [debouncedZoneSearchText, setDebouncedZoneSearchText] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState<Zone | null>(null);
  const [selectedZoneForAssets, setSelectedZoneForAssets] = useState<Zone | null>(null);
  const [zoneAssetsPageNumber, setZoneAssetsPageNumber] = useState(1);
  const [zoneAckInfoByGateway, setZoneAckInfoByGateway] = useState<any[]>([]);
  const [isUnAckModalOpen, setIsUnAckModalOpen] = useState(false);
  const [selectedZoneIdForUnAck, setSelectedZoneIdForUnAck] = useState<number | null>(null);
  const [zoneUnAckInfoPageNumber, setZoneUnAckInfoPageNumber] = useState(1);
  const [zoneUnAckInfoPageSize, setZoneUnAckInfoPageSize] = useState(10);
  const [zoneUnAckInfoSearchText, setZoneUnAckInfoSearchText] = useState("");
  const [debouncedZoneUnAckInfoSearchText, setDebouncedZoneUnAckInfoSearchText] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedZoneUnAckInfoSearchText(zoneUnAckInfoSearchText);
    }, 1000);
    return () => clearTimeout(timer);
  }, [zoneUnAckInfoSearchText]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedZoneSearchText(zoneSearchText);
    }, 1000);
    return () => clearTimeout(timer);
  }, [zoneSearchText]);

  const [zoneUnAckInfoSortBy, setZoneUnAckInfoSortBy] = useState("CommandSentOn");
  const [zoneUnAckInfoSortDirection, setZoneUnAckInfoSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [zoneUnAckInfoColumns, setZoneUnAckInfoColumns] = useState([
    { title: "Asset Code", label: "assetCode", isSorted: true },
    { title: "Zone Type", label: "zoneType" },
    { title: "Event Id", label: "eventId" },
    { title: "Command Sent On", label: "commandSentOn", isSorted: true, type: "datetime" },
  ]);

  useEffect(() => {
    const routeState = (location.state || {}) as any;
    const selection = routeState.zoneFloorSelection;
    const floorplanBack = routeState.zoneFloorBack;
    if (!selection && !floorplanBack) return;

    setEditingZone((selection || floorplanBack).zone || null);
    setInitialAssignedAssets(
      selection?.selectedAssets || floorplanBack?.assignedAssets || [],
    );
    setIsAddModalOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const {
    data: zonesResponse,
    isLoading: isLoadingZones,
    isFetching: isFetchingZones,
    refetch: refetchZones,
  } = useQuery({
    queryKey: ["zonesByGateway", gatewayCode, pageNumber, debouncedZoneSearchText],
    queryFn: ({ signal }) =>
      getZonesByGateway(
        gatewayCode || "",
        { pageNumber, pageSize: PAGE_SIZE, searchText: debouncedZoneSearchText },
        signal,
      ),
    enabled: !!gatewayCode,
  });

  const {
    data: zoneUnAckInfoResponse,
    isLoading: isLoadingZoneUnAckInfo,
    refetch: refetchZoneUnAckInfo,
  } = useQuery({
    queryKey: [
      "zoneUnAckInfoByGateway",
      gatewayCode,
      selectedZoneIdForUnAck,
      zoneUnAckInfoPageNumber,
      zoneUnAckInfoPageSize,
      debouncedZoneUnAckInfoSearchText,
      zoneUnAckInfoSortBy,
      zoneUnAckInfoSortDirection,
    ],
    queryFn: ({ signal }) =>
      getZoneUnAckInfoByGateway({
        gatewayCode: gatewayCode || "",
        pageNumber: zoneUnAckInfoPageNumber,
        pageSize: zoneUnAckInfoPageSize,
        searchText: debouncedZoneUnAckInfoSearchText,
        sortBy: zoneUnAckInfoSortBy,
        sortDirection: zoneUnAckInfoSortDirection,
        zoneId: selectedZoneIdForUnAck ?? undefined,
      }),
    enabled: isUnAckModalOpen && !!gatewayCode,
  });

  const {
    data: zoneAssetsResponse,
    isLoading: isLoadingZoneAssets,
  } = useQuery({
    queryKey: ["gatewayZoneAssets", selectedZoneForAssets?.id, zoneAssetsPageNumber],
    queryFn: ({ signal }) =>
      getGatewayZoneAssets(selectedZoneForAssets!.id, {
        pageNumber: zoneAssetsPageNumber,
        pageSize: ZONE_ASSETS_PAGE_SIZE,
        sortBy: "AssetCode",
        sortDirection: "DESC",
      }, signal),
    enabled: selectedZoneForAssets != null,
  });

  const zones: Zone[] = (zonesResponse?.data || []).map((z: any) => ({
    ...z,
    id: z.id || z.zoneId,
    name: z.zoneName || z.name || `Zone ${z.zoneId || z.id}`,
    zoneId: z.zoneId || z.id,
    isAcknowledged: z.isAcknowledged,
  }));

  const zoneAckInfoByZone = useMemo(() => {
    const map: Record<string, any> = {};
    zoneAckInfoByGateway.forEach((item: any) => {
      const key = item.zoneId != null ? String(item.zoneId) : undefined;
      if (key) {
        map[key] = item;
      }
    });
    return map;
  }, [zoneAckInfoByGateway]);

  /* Temporarily disabled with the group acknowledgement toolbar indicator.
  const groupZoneStatus = useMemo(() => {
    if (!zoneAckInfoByGateway.length) return null;

    const hasRed = zoneAckInfoByGateway.some((item: any) => item.color === "Red");
    const hasYellow = zoneAckInfoByGateway.some((item: any) => item.color === "Yellow");

    if (hasRed && !hasYellow) {
      return { color: "#dc3545", clickable: true };
    }
    if (hasYellow && !hasRed) {
      return { color: "#cc8d00b3", clickable: false };
    }
    return null;
  }, [zoneAckInfoByGateway]);

  const groupZoneStatusTitle = useMemo(() => {
    if (!zoneAckInfoByGateway.length) return "";

    const redZone = zoneAckInfoByGateway.find((item: any) => item.color === "Red");
    if (redZone?.statusMessage) return redZone.statusMessage;

    const yellowZone = zoneAckInfoByGateway.find((item: any) => item.color === "Yellow");
    if (yellowZone?.statusMessage) return yellowZone.statusMessage;

    return "";
  }, [zoneAckInfoByGateway]);
  */

  const totalRecords = (zonesResponse as any)?.numberOfRecords ?? zones.length;

  useEffect(() => {
    if (assetDetails?.assetCode) {
      setGatewayCode(assetDetails.assetCode);
    } else if (assetCode) {
      setGatewayCode(assetCode);
    }
  }, [assetDetails, assetCode]);

  useEffect(() => {
    setPageNumber(1);
    setZoneSearchText("");
    setDebouncedZoneSearchText("");
    /* setSelectedZoneIds([]); */
  }, [gatewayCode]);

  const refreshZoneAckInfo = useCallback(async () => {
    if (!gatewayCode) return;

    try {
      const res = await getZoneAckInfoByGateway(gatewayCode);
      setZoneAckInfoByGateway(res?.data || []);
    } catch (error) {
      console.error("Failed to load zone ack info: ", error);
    }
  }, [gatewayCode]);

  useEffect(() => {
    if (!gatewayCode || totalRecords === 0) return;

    refreshZoneAckInfo();
    const interval = setInterval(refreshZoneAckInfo, 15000);
    return () => clearInterval(interval);
  }, [gatewayCode, totalRecords, refreshZoneAckInfo]);

  const handleAddZone = () => {
    setEditingZone(null);
    setInitialAssignedAssets(undefined);
    setIsAddModalOpen(true);
  };

  const handleEditZone = (zone: Zone) => {
    setEditingZone(zone);
    setInitialAssignedAssets(undefined);
    setIsAddModalOpen(true);
  };

  const handleDeleteZone = (zone: Zone) => {
    setZoneToDelete(zone);
    setIsDeleteModalOpen(true);
  };

  const handleViewZoneAssets = (zone: Zone) => {
    setZoneAssetsPageNumber(1);
    setSelectedZoneForAssets(zone);
  };

  const handleCloseZoneAssets = () => setSelectedZoneForAssets(null);

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setZoneToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!assetCode || !zoneToDelete) return;

    try {
      await deleteGatewayZone(zoneToDelete?.id);
      /* setSelectedZoneIds((current) => current.filter((zoneId) => zoneId !== zoneToDelete.id)); */
      await refetchZones();
    } catch (error: any) {
      console.error("Failed to delete zone: ", error);
      displayToast(
        error?.response?.data?.message || "Failed to delete zone.",
        "red"
      );
    } finally {
      handleCloseDeleteModal();
    }
  };

  const deleteModalActionConfig = [
    {
      label: "Yes",
      event: handleConfirmDelete,
      variant: "primary",
    },
  ];

  const handleSettings = (zone: Zone) => {
    if (zoneConfigurationStates[zone.id]?.status === "pending") return;
    setConfiguringZone(zone);
  };

  const handleZoneConfigurationStarted = startZoneConfiguration;
  const handleZoneConfigurationFailed = failZoneConfiguration;

  const handleCloseZoneConfiguration = () => setConfiguringZone(null);

  const handleNavigate = (zone: Zone) => {
    displayToast(`Navigate for "${zone.name}" - coming soon.`, "info");
  };

  const handleSendSingleZoneToGateway = async (zone: Zone) => {
    if (!gatewayCode) return;

    try {
      const response = await saveToGatewayZone(gatewayCode, [zone.id]);
      if (response?.status === 200) {
        await refreshZoneAckInfo();
      }
    } catch (error: any) {
      displayToast(error?.response?.data?.message || "Commands didn't send successfully.", "red");
    }
  };

  /* Temporarily disabled with the zone-list send actions.
  const handleSendToGateway = async (zoneIds: number[]) => {
    if (!gatewayCode || !zoneIds.length) return false;

    try {
      const response = await saveToGatewayZone(gatewayCode, zoneIds);
      if (response?.status === 200) {
        await refreshZoneAckInfo();
      }
      return true;
    } catch (error: any) {
      displayToast(error?.response?.data?.message || "Commands didn't send successfully.", "red");
      return false;
    }
  };

  const handleSendSingleZoneToGateway = (zone: Zone) => {
    handleSendToGateway([zone.id]);
  };

  const handleZoneSelectionChange = (zoneId: number, isSelected: boolean) => {
    setSelectedZoneIds((current) => {
      if (isSelected) return current.includes(zoneId) ? current : [...current, zoneId];
      return current.filter((selectedZoneId) => selectedZoneId !== zoneId);
    });
  };

  const handleSendSelectedZonesToGateway = async () => {
    const zoneIdsToSend = [...selectedZoneIds];
    const wasSent = await handleSendToGateway(zoneIdsToSend);
    if (wasSent) {
      setSelectedZoneIds((current) => current.filter((zoneId) => !zoneIdsToSend.includes(zoneId)));
    }
  };
  */

  const handleUnacknowledgedClick = (zone: Zone) => {
    setSelectedZoneIdForUnAck(zone.id);
    setIsUnAckModalOpen(true);
  };

  /* Temporarily disabled with the group acknowledgement toolbar indicator.
  const handleGroupUnacknowledgedClick = () => {
    setSelectedZoneIdForUnAck(null);
    setIsUnAckModalOpen(true);
  };
  */

  const handleCloseUnAckModal = () => {
    setIsUnAckModalOpen(false);
    setSelectedZoneIdForUnAck(null);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setEditingZone(null);
    setInitialAssignedAssets(undefined);
  };

  const handleSaved = () => {
    refetchZones();
  };

  const handlePageChange = (page: number) => {
    setPageNumber(page);
  };

  const handleZoneSearchTextChange = (searchText: string) => {
    setZoneSearchText(searchText);
    setPageNumber(1);
  };

  const handleZoneUnAckInfoPageChange = (page: number) => {
    setZoneUnAckInfoPageNumber(page);
  };

  const handleZoneUnAckInfoSearch = (searchText: string) => {
    setZoneUnAckInfoSearchText(searchText);
    setZoneUnAckInfoPageNumber(1);
  };

  const handleChangeSortZoneUnAckInfo = (sortBy: string, sortDirection: string) => {
    if (!sortBy || !sortDirection) return;
    setZoneUnAckInfoSortBy(sortBy);
    setZoneUnAckInfoSortDirection(sortDirection.toUpperCase() as "ASC" | "DESC");
  };

  return (
    <div className="zones-page configuration-section">
      <div className="zones-header">
        <h1>Zones</h1>
        <p>Manage zone settings for lighting infrastructure and spatial groups.</p>
      </div>

      <ZoneCardList
        zones={zones}
        onEdit={handleEditZone}
        onDelete={handleDeleteZone}
        onSettings={handleSettings}
        onNavigate={handleNavigate}
        onSendToGateway={handleSendSingleZoneToGateway}
        onViewAssets={handleViewZoneAssets}
        {...{
          // onSendSelectedToGateway: handleSendSelectedZonesToGateway,
          // selectedZoneIds,
          // onZoneSelectionChange: handleZoneSelectionChange,
        }}
        onUnacknowledgedClick={handleUnacknowledgedClick}
        zoneAckInfoByZone={zoneAckInfoByZone}
        {...{
          // onGroupUnacknowledgedClick: handleGroupUnacknowledgedClick,
          // groupZoneStatus,
          // groupZoneStatusTitle,
        }}
        isLoading={isLoadingZones || isFetchingZones}
        pageNumber={pageNumber}
        pageSize={PAGE_SIZE}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        zoneCount={totalRecords}
        onAddZone={handleAddZone}
        searchText={zoneSearchText}
        onSearchTextChange={handleZoneSearchTextChange}
        zoneConfigurationStates={zoneConfigurationStates}
      />

      <ModalComponent
        showModal={selectedZoneForAssets != null}
        handleClose={handleCloseZoneAssets}
        size="lg"
        headerTitle={
          <div>
            <div>Zone Assets</div>
            {selectedZoneForAssets && (
              <div style={{ fontSize: "0.85rem", color: "#64748b", marginTop: "2px" }}>
                {selectedZoneForAssets.name}
              </div>
            )}
          </div>
        }
        isViewMode
      >
        <NewTableComponent
          tableHeaders={[
            {
              title: "Asset Code",
              label: "assetCode",
              excludeSort: true,
              renderProps: (asset: { assetCode: string }) => (
                <button
                  type="button"
                  className="zone-assets__asset-code-link"
                  onClick={() =>
                    navigate(`/asset/${btoa(asset.assetCode)}/configuration/general-configuration`)
                  }
                >
                  {asset.assetCode}
                </button>
              ),
            },
            { title: "Display Name", label: "sensorDisplayName", excludeSort: true },
          ]}
          data={zoneAssetsResponse?.data || []}
          totalNumberOfRecords={zoneAssetsResponse?.numberOfRecords || 0}
          pageSize={ZONE_ASSETS_PAGE_SIZE}
          current={zoneAssetsPageNumber}
          onChangePage={setZoneAssetsPageNumber}
          isLoadingData={isLoadingZoneAssets}
          emptyConfig={{ title: "No Assets Found", description: "This zone has no assets." }}
          wrapperHeight="400px"
          count={6}
        />
      </ModalComponent>

      <ZoneFormModal
        showModal={isAddModalOpen}
        handleClose={handleCloseModal}
        zone={editingZone}
        gatewayCode={gatewayCode}
        initialAssignedAssets={initialAssignedAssets}
        onSaved={handleSaved}
      />

      {configuringZone && (
        <ZoneGeneralConfigurationModal
          key={configuringZone.id}
          zone={configuringZone}
          vendor={assetDetails?.vendor}
          assetCode={gatewayCode || null}
          onClose={handleCloseZoneConfiguration}
          onApplyStarted={handleZoneConfigurationStarted}
          onApplyFailed={handleZoneConfigurationFailed}
          onSaved={handleSaved}
        />
      )}

      {isDeleteModalOpen && (
        <ModalComponent
          isConformationModal
          showModal={isDeleteModalOpen}
          handleClose={handleCloseDeleteModal}
          headerTitle="Delete Zone"
          actionConfig={deleteModalActionConfig}
        >
          <h5 className="text-center">
            Are you sure you want to delete <b className="text-danger">{zoneToDelete?.name}</b>?
          </h5>
        </ModalComponent>
      )}

      {isUnAckModalOpen && (
        <ModalComponent
          showModal={isUnAckModalOpen}
          handleClose={handleCloseUnAckModal}
          headerTitle="Unacknowledged Commands"
          isViewMode={true}
        >
          <div className="d-flex gap-3 justify-content-end">
            <SearchBarComponent
              placeholder="Search Event"
              getSearch={handleZoneUnAckInfoSearch}
            />
          </div>
          <div className="my-3">
            <NewTableComponent
              tableHeaders={zoneUnAckInfoColumns}
              emptyConfig={{ title: "No Data Found", description: "" }}
              data={zoneUnAckInfoResponse?.data || []}
              totalNumberOfRecords={zoneUnAckInfoResponse?.numberOfRecords || 0}
              pageSize={zoneUnAckInfoPageSize}
              current={zoneUnAckInfoPageNumber}
              onChangePage={handleZoneUnAckInfoPageChange}
              onChangeSort={handleChangeSortZoneUnAckInfo}
              isLoadingData={isLoadingZoneUnAckInfo}
              wrapperHeight="340px"
              count={6}
            />
          </div>
        </ModalComponent>
      )}
    </div>
  );
};

const ZonesComponent = () => (
  <ZoneConfigurationAckProvider>
    <ZonesContent />
  </ZoneConfigurationAckProvider>
);

export default ZonesComponent;
